import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import OnboardingLogo from "../../components/onboarding/OnboardingLogo";
import { onboardingShell } from "../../components/onboarding/styles";
import { API_BASE } from "../../lib/api";
import { setCurrentCustomer } from "../../lib/customerSession";
import { supabase } from "../../lib/supabaseClient";

/**
 * Lands at {window.location.origin}/auth/callback?code=... after the user
 * completes Google auth via Supabase Auth's Google provider (2026-08 —
 * replaces the old Emergent-managed broker). Supabase's PKCE flow hands us
 * a one-time `code`; we exchange it for a Supabase session, then send the
 * verified Supabase access_token to OUR OWN backend, which verifies it
 * against Supabase, finds/creates the customer by email, and issues our
 * existing session_token cookie — the rest of the funnel (collect-phone ->
 * business profile -> dashboard) is completely unchanged.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    // useRef (not useState) processed-flag, set synchronously — prevents a
    // double-exchange under React StrictMode / fast re-renders.
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    (async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const code = params.get("code");
        const oauthError = params.get("error_description");
        if (oauthError) throw new Error(oauthError);

        if (code) {
          const { error: exchangeErr } = await supabase.auth.exchangeCodeForSession(code);
          if (exchangeErr) throw exchangeErr;
        }
        const { data, error: sessionErr } = await supabase.auth.getSession();
        if (sessionErr || !data?.session) throw sessionErr || new Error("no_supabase_session");

        const res = await fetch(`${API_BASE}/auth/google/session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ access_token: data.session.access_token }),
        });
        if (!res.ok) throw new Error("exchange_failed");
        const data2 = await res.json();

        setCurrentCustomer(data2.customer);
        // Strip the query string so a page refresh doesn't re-process the
        // same one-time code.
        window.history.replaceState(null, "", window.location.pathname);

        if (data2.phone_required) {
          try { sessionStorage.removeItem("post_login_redirect"); } catch (_e) { /* ignore */ }
          navigate("/collect-phone", { replace: true });
        } else if (data2.profile_required) {
          try { sessionStorage.removeItem("post_login_redirect"); } catch (_e) { /* ignore */ }
          sessionStorage.setItem("onboarding_email", data2.customer.email);
          sessionStorage.setItem("onboarding_customer_id", data2.customer.id);
          navigate("/business-profile", { replace: true });
        } else {
          // Returning, fully-onboarded user — Command Center is the default
          // post-login landing page (2026-11), or the deep-linked protected
          // route GoogleButton stashed before the OAuth redirect.
          let dest = "/command-center";
          try {
            const stashed = sessionStorage.getItem("post_login_redirect");
            if (stashed) dest = stashed;
            sessionStorage.removeItem("post_login_redirect");
          } catch (_e) { /* ignore */ }
          navigate(dest, { replace: true });
        }
      } catch (_e) {
        setError("Could not complete Google sign-in. Please try again.");
        setTimeout(() => navigate("/login", { replace: true }), 2000);
      }
    })();
  }, [navigate]);

  // Playbook: process silently, no loading UI — only surface a message on
  // the rare failure path.
  return (
    <div data-testid="auth-callback-page" style={onboardingShell}>
      <div style={{ marginBottom: 24 }}><OnboardingLogo /></div>
      {error && (
        <p data-testid="auth-callback-error" style={{ color: "#DC2626", fontSize: 14 }}>{error}</p>
      )}
    </div>
  );
}
